// Windows "ALT-0XXX" character codes — holding Alt and typing a 4-digit
// decimal code (with leading zero) types the ANSI/Windows-1252 character at
// that code point. That codepage matches Unicode everywhere except 0x80-0x9F
// (curly quotes, dashes, trademark, etc.), so decoding is: look up that one
// divergent range, otherwise treat the number as a raw Unicode code point.
// (The older 3-digit "ALT-XXX" OEM/437 codes are a different, legacy
// codepage and aren't supported here.)
const CP1252_C1_OVERRIDES = {
  128: 0x20ac, // €
  130: 0x201a, // ‚
  131: 0x0192, // ƒ
  132: 0x201e, // „
  133: 0x2026, // …
  134: 0x2020, // †
  135: 0x2021, // ‡
  136: 0x02c6, // ˆ
  137: 0x2030, // ‰
  138: 0x0160, // Š
  139: 0x2039, // ‹
  140: 0x0152, // Œ
  142: 0x017d, // Ž
  145: 0x2018, // '
  146: 0x2019, // '
  147: 0x201c, // "
  148: 0x201d, // "
  149: 0x2022, // •
  150: 0x2013, // –
  151: 0x2014, // —
  152: 0x02dc, // ˜
  153: 0x2122, // ™
  154: 0x0161, // š
  155: 0x203a, // ›
  156: 0x0153, // œ
  158: 0x017e, // ž
  159: 0x0178, // Ÿ
};

export function formatAltCode(code) {
  return `ALT-${String(code).padStart(4, '0')}`;
}

// Accepts "ALT-0153", "alt+0153", "0153", or "153" and returns the bare
// digit string, or null if the input isn't a plausible code.
export function parseAltCodeInput(input) {
  if (!input) return null;
  const match = String(input).trim().match(/^(?:alt[\s-]?\+?)?(\d{1,7})$/i);
  return match ? match[1] : null;
}

export function charFromAltCode(code) {
  const n = Number(code);
  if (!Number.isInteger(n) || n < 32 || n > 0x10ffff) return null;
  if (n >= 128 && n <= 159) {
    const cp = CP1252_C1_OVERRIDES[n];
    return cp ? String.fromCodePoint(cp) : null;
  }
  return String.fromCodePoint(n);
}

// Curated picker grid — the ALT codes people actually reach for on product
// labels/signage (trademark/legal marks, typographic punctuation, math &
// currency, a handful of accented letters). Not exhaustive; the manual
// "ALT-XXXX" field in SpecialCharDialog covers anything else.
export const COMMON_SPECIAL_CHARS = [
  { code: '0153', name: 'Trademark' },
  { code: '0174', name: 'Registered' },
  { code: '0169', name: 'Copyright' },
  { code: '0176', name: 'Degree' },
  { code: '0177', name: 'Plus-minus' },
  { code: '0189', name: 'One half' },
  { code: '0188', name: 'One quarter' },
  { code: '0190', name: 'Three quarters' },
  { code: '0215', name: 'Multiplication' },
  { code: '0247', name: 'Division' },
  { code: '0167', name: 'Section' },
  { code: '0182', name: 'Paragraph' },
  { code: '0149', name: 'Bullet' },
  { code: '0150', name: 'En dash' },
  { code: '0151', name: 'Em dash' },
  { code: '0133', name: 'Ellipsis' },
  { code: '0145', name: 'Left single quote' },
  { code: '0146', name: 'Right single quote' },
  { code: '0147', name: 'Left double quote' },
  { code: '0148', name: 'Right double quote' },
  { code: '0128', name: 'Euro' },
  { code: '0163', name: 'Pound' },
  { code: '0165', name: 'Yen' },
  { code: '0162', name: 'Cent' },
  { code: '0181', name: 'Micro' },
  { code: '0198', name: 'AE ligature' },
  { code: '0231', name: 'C cedilla' },
  { code: '0241', name: 'N tilde' },
  { code: '0252', name: 'U umlaut' },
  { code: '0223', name: 'Sharp S' },
].map((entry) => ({ ...entry, char: charFromAltCode(entry.code) }));
